import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { useNavigate } from "react-router-dom";
import NoCopyWrapper from "../../utils/NoCopyWrapper";
import NoZoomWrapper from "../../utils/NoZoomWrapper";
import slugify from "slugify";

import "swiper/css";
import "swiper/css/pagination";

export default function Slider() {
  const [images, setImages] = useState([]);
  const navigate = useNavigate();

  /* ================= RESPONSIVE STATES ================= */
  const [isVerySmall, setIsVerySmall] = useState(window.innerWidth <= 360);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth > 360 && window.innerWidth <= 576
  );
  const [isTablet, setIsTablet] = useState(
    window.innerWidth > 576 && window.innerWidth <= 991
  );

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsVerySmall(w <= 360);
      setIsMobile(w > 360 && w <= 576);
      setIsTablet(w > 576 && w <= 991);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ================= FETCH BANNERS ================= */
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch(
          "https://network.rentel.in/banners/get?limit=20&skip=0&isActive=true"
        );
        const data = await res.json();
        if (data?.banners) setImages(data.banners);
      } catch (err) {
        console.error("Banner fetch error:", err);
      }
    };

    fetchBanners();
  }, []);

  /* ================= CLICK HANDLER ================= */
  const handleBannerClick = useCallback(
    (banner) => {
      const product = banner?.productId;
      const externalLink = banner?.externalLink;

      if (externalLink) {
        window.open(externalLink, "_blank", "noopener noreferrer");
        return;
      }

      if (product && product._id) {
        const productTitle = slugify(product.title || "product", {
          lower: true,
          strict: true,
        });

        navigate(
          `/${product.category}/${product.subCategory}/${productTitle}/${product._id}`,
          { state: { product } }
        );
      }
    },
    [navigate]
  );

  /* ================= SWIPER CONFIG ================= */
  const swiperConfig = useMemo(
    () => ({
      spaceBetween: isVerySmall ? 8 : isMobile ? 12 : 24,
      centeredSlides: true,
      autoplay: {
        delay: 2500,
        disableOnInteraction: false,
      },
      pagination: { clickable: true },
      loop: true,
      modules: [Autoplay, Pagination],
      className: "mySwiperf",
    }),
    [isVerySmall, isMobile]
  );

  /* ================= BANNER SIZE ================= */
  const bannerHeight = isVerySmall
    ? "150px"
    : isMobile
      ? "180px"
      : isTablet
        ? "260px"
        : "300px"; // laptop & desktop

  const bannerTop =
    isVerySmall || isMobile
      ? "10px"
      : isTablet
        ? "101px"
        : "100px"; // laptop clean

  const isSmallDevice = isVerySmall || isMobile || isTablet;

  /* ================= RENDER ================= */
  return (
    <NoZoomWrapper>
      <NoCopyWrapper>
        <div className="slider-space">
          <section
            className="container"
            role="region"
            aria-label="Banner carousel"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 12px",
              borderRadius: "20px",
            }}
          >
            <Swiper {...swiperConfig}>
              {images.length > 0 ? (
                images.map((item, index) => (
                  <SwiperSlide
                    key={item._id || index}
                    onClick={() => handleBannerClick(item)}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={
                        isSmallDevice
                          ? item.mobileImage || item.webImage
                          : item.webImage
                      }
                      alt={`Banner ${index + 1}`}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: bannerHeight,
                        objectFit: "cover",
                        borderRadius: "15px",
                        marginTop: bannerTop,
                      }}
                    />
                  </SwiperSlide>
                ))
              ) : (
                <SwiperSlide>
                  <p style={{ textAlign: "center" }}>
                    No banners available
                  </p>
                </SwiperSlide>
              )}
            </Swiper>
          </section>
        </div>
      </NoCopyWrapper>
    </NoZoomWrapper>
  );
}
